// component/landing/HeroPage.js
import React from 'react';
import { Container, Row, Col, Button, Card, Navbar, Nav } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaVideo,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaPlay,
  FaStar,
  FaGoogle,
  FaMicrosoft,
  FaSlack
} from 'react-icons/fa';

const HeroPage = () => {
  const features = [
    {
      icon: <FaCalendarAlt />,
      title: "Smart Scheduling",
      description: "Automatically find the perfect time for meetings without back-and-forth emails."
    },
    {
      icon: <FaVideo />,
      title: "Video Conferencing",
      description: "Integrated video calls with all popular platforms like Zoom, Google Meet, and Teams."
    },
    {
      icon: <FaUsers />,
      title: "Team Coordination",
      description: "Coordinate multiple team members' availability for group meetings effortlessly."
    },
    {
      icon: <FaClock />,
      title: "Time Zone Intelligence",
      description: "Automatically detect and display times in your guests' time zones."
    }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Marketing Manager",
      company: "TechCorp",
      content: "This platform saved our team hours every week in scheduling meetings.",
      rating: 5
    },
    {
      name: "Rahul Verma",
      role: "Sales Director",
      company: "SalesForce Inc",
      content: "The most intuitive scheduling tool I've ever used. Clients love it!",
      rating: 5
    },
    {
      name: "Anita Patel",
      role: "Freelance Designer",
      company: "Creative Studio",
      content: "Perfect for managing client meetings across different time zones.",
      rating: 5
    }
  ];

  const renderStars = (count) => {
    return Array.from({ length: count }, (_, i) => (
      <FaStar key={i} className="text-warning" />
    ));
  };

  return (
    <div className="hero-page">
      {/* Navigation */}
      <Navbar expand="lg" className="navbar-light bg-white py-3">
        <Container>
          <Navbar.Brand className="fw-bold fs-3 text-primary">
            <FaCalendarAlt className="me-2" />
            Meetify
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link href="#features" className="text-dark mx-3">Features</Nav.Link>
              <Nav.Link href="#pricing" className="text-dark mx-3">Pricing</Nav.Link>
              <Nav.Link href="#enterprise" className="text-dark mx-3">Enterprise</Nav.Link>
              <Button variant="primary" size="sm" className="me-2" href="/login">Sign In</Button>
              <Button variant="primary" size="sm" href="/login">Get Started Free</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section className="hero-section py-4 py-lg-5">
        <Container>
          <Row className="align-items-center min-vh-80">
            <Col lg={6} className="order-lg-1 order-2">
              <div className="hero-content">
                <div className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill mb-3">
                  🚀 No credit card required
                </div>
                <h1 className="display-4 fw-bold text-dark mb-4">
                  Scheduling
                  <span className="text-primary"> Made</span>
                  <br />
                  <span className="text-primary">Simple</span>
                </h1>
                <p className="lead text-muted mb-4">
                  Eliminate the scheduling back-and-forth. Let your clients, teammates,
                  and prospects book meetings with you in just a few clicks.
                </p>
                <div className="hero-buttons d-flex gap-3 mb-4 flex-column flex-lg-row">
                  <Button variant="primary" size="lg" className="px-4 py-3 w-100 w-lg-auto" href="/login">
                    Start Scheduling Free
                    <FaArrowRight className="ms-2" />
                  </Button>
                  <Button variant="outline-dark" size="lg" className="px-4 py-3 w-100 w-lg-auto">
                    <FaPlay className="me-2" />
                    Watch Demo
                  </Button>
                </div>
                {/* <div className="trust-badges d-flex align-items-center flex-wrap gap-3">
                  <span className="text-muted">Trusted by teams at:</span>
                  <div className="d-flex gap-3">
                    <FaGoogle className="text-muted" size={20} />
                    <FaMicrosoft className="text-muted" size={20} />
                    <FaSlack className="text-muted" size={20} />
                  </div>
                </div> */}
              </div>
            </Col>
            <Col lg={6} className="order-lg-2 order-1 mb-4 mb-lg-0">
              <div className="hero-visual">
                {/* Floating cards for desktop */}
                <div className="floating-card card-1 d-lg-block d-none">
                  <Card className="modern-card border-0 shadow-lg">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center mb-2">
                        <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-2">
                          <FaUsers className="text-white" size={14} />
                        </div>
                        <span className="fw-semibold">Team Meeting</span>
                      </div>
                      <div className="text-muted small">30 min • Video Call</div>
                    </Card.Body>
                  </Card>
                </div>
                <div className="floating-card card-2 d-lg-block d-none">
                  <Card className="modern-card border-0 shadow-lg">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center mb-2">
                        <div className="avatar-sm bg-success rounded-circle d-flex align-items-center justify-content-center me-2">
                          <FaCalendarAlt className="text-white" size={14} />
                        </div>
                        <span className="fw-semibold">Client Call</span>
                      </div>
                      <div className="text-muted small">45 min • Google Meet</div>
                    </Card.Body>
                  </Card>
                </div>

                {/* Stacked cards for mobile */}
                <div className="mobile-meeting-cards d-block d-lg-none mb-3">
                  <Card className="modern-card border-0 shadow-lg mb-2">
                    <Card.Body className="p-3 text-center">
                      <div className="d-flex align-items-center justify-content-center mb-2">
                        <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-2">
                          <FaUsers className="text-white" size={14} />
                        </div>
                        <span className="fw-semibold">Team Meeting</span>
                      </div>
                      <div className="text-muted small">30 min • Video Call</div>
                    </Card.Body>
                  </Card>
                  <Card className="modern-card border-0 shadow-lg">
                    <Card.Body className="p-3 text-center">
                      <div className="d-flex align-items-center justify-content-center mb-2">
                        <div className="avatar-sm bg-success rounded-circle d-flex align-items-center justify-content-center me-2">
                          <FaCalendarAlt className="text-white" size={14} />
                        </div>
                        <span className="fw-semibold">Client Call</span>
                      </div>
                      <div className="text-muted small">45 min • Google Meet</div>
                    </Card.Body>
                  </Card>
                </div>

                {/* Dashboard image - shown on both mobile and desktop */}
                <div className="dashboard-image">
                  <img
                    src="/dashboard1.png"
                    alt="Meeting Dashboard"
                    className="img-fluid rounded-3 shadow w-100"
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section id="features" className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold text-dark mb-3">
                Why Teams Choose Meetify
              </h2>
              <p className="lead text-muted">
                Streamline your scheduling process with powerful features designed for modern teams.
              </p>
            </Col>
          </Row>
          <Row>
            {features.map((feature, index) => (
              <Col key={index} xs={12} md={6} lg={3} className="mb-4">
                <Card className="modern-card border-0 h-100 text-center">
                  <Card.Body className="p-4">
                    <div className="feature-icon bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '80px', height: '80px' }}>
                      <div className="text-primary fs-2">
                        {feature.icon}
                      </div>
                    </div>
                    <h5 className="fw-bold text-dark mb-3">{feature.title}</h5>
                    <p className="text-muted">{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Testimonials */}
      {/* <section className="py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold text-dark mb-3">
                Loved by Thousands
              </h2>
              <p className="lead text-muted">
                Join 50,000+ professionals who save time with smart scheduling.
              </p>
            </Col>
          </Row>
          <Row>
            {testimonials.map((testimonial, index) => (
              <Col key={index} xs={12} lg={4} className="mb-4">
                <Card className="modern-card border-0 h-100">
                  <Card.Body className="p-4">
                    <div className="mb-3">
                      {renderStars(testimonial.rating)}
                    </div>
                    <p className="text-muted mb-4">"{testimonial.content}"</p>
                    <div className="d-flex align-items-center">
                      <div className="avatar bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: '40px', height: '40px' }}>
                        <span className="text-white fw-bold">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-0">{testimonial.name}</h6>
                        <small className="text-muted">{testimonial.role}, {testimonial.company}</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section> */}

      {/* CTA Section */}
      <section className="py-5 bg-primary">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold text-white mb-3">
                Ready to Transform Your Scheduling?
              </h2>
              <p className="lead text-white mb-4">
                Join thousands of professionals saving hours every week.
              </p>
              <Button variant="light" size="lg" className="px-5 py-3 fw-bold mb-3" href="/login">
                Get Started Free
                <FaArrowRight className="ms-2" />
              </Button>
              <p className="text-white-50 small">
                No credit card required • Free forever plan
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 bg-dark text-white">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <FaCalendarAlt className="me-2 text-primary" />
                <span className="fw-bold">Meetify</span>
              </div>
              <p className="text-white-50 mb-0 small mt-2">
                © {new Date().getFullYear()} Meetify. All rights reserved.
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <a href="/PrivacyPolicy" className="text-white-50 text-decoration-none me-3">Privacy</a>
              <a href="/TermsAndConditions" className="text-white-50 text-decoration-none me-3">Terms</a>
              <a href="/helpcenter" className="text-white-50 text-decoration-none">Contact</a>
            </Col>
          </Row>
        </Container>
      </footer>

      <style>{`
        .hero-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        .min-vh-80 {
          min-height: 80vh;
        }
        
        .hero-section {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }
        
        .hero-visual {
          position: relative;
          height: 400px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .floating-card {
          position: absolute;
          z-index: 2;
        }
        
        .card-1 {
          top: 10%;
          left: 0;
          animation: float 3s ease-in-out infinite;
        }
        
        .card-2 {
          bottom: 20%;
          right: 0;
          animation: float 3s ease-in-out infinite 1.5s;
        }
        
        .dashboard-image {
          position: relative;
          z-index: 1;
        }
        
        .dashboard-image img {
          max-height: 250px;
          object-fit: cover;
        }
        
        .mobile-meeting-cards {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .avatar-sm {
          width: 30px;
          height: 30px;
        }
        
        .avatar {
          width: 50px;
          height: 50px;
        }
        
        .feature-icon {
          transition: transform 0.3s ease;
        }
        
        .feature-icon:hover {
          transform: scale(1.1);
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .modern-card {
          transition: all 0.3s ease;
        }
        
        .modern-card:hover {
          transform: translateY(-5px);
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 10px;
          font-weight: 600;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
        }
        
        @media (max-width: 768px) {
          .display-4 {
            font-size: 2.5rem;
            line-height: 1.2;
          }
          
          .hero-section {
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
          }
          
          .hero-visual {
            height: auto;
            margin-top: 2rem;
            flex-direction: column;
            align-items: center;
          }
          
          .hero-buttons {
            flex-direction: column !important;
          }
          
          .hero-buttons .btn {
            width: 100% !important;
            margin-bottom: 1rem;
          }
          
          .trust-badges {
            justify-content: center;
            text-align: center;
          }
          
          .trust-badges .d-flex {
            justify-content: center;
          }
          
          .navbar-brand {
            font-size: 1.5rem;
          }
          
          .nav-link {
            padding: 0.5rem 1rem;
          }
          
          .floating-card {
            display: none !important;
          }
          
          .dashboard-image img {
            max-height: 200px;
          }
          
          .mobile-meeting-cards .modern-card {
            width: 100%;
            max-width: 300px;
          }
        }
        
        @media (max-width: 576px) {
          .display-4 {
            font-size: 2rem;
          }
          
          .hero-visual {
            height: auto;
          }
          
          .display-5 {
            font-size: 1.75rem;
          }
          
          .mobile-meeting-cards {
            gap: 1rem;
          }
          
          .dashboard-image img {
            max-height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default HeroPage;